// PidDisplayModal.jsx — Add/Edit surface for PID Displays on the shared PidModal scaffold.
// Replaces DisplayDetailPanel's bespoke chrome; the body — image carousel with swipe +
// lightbox, upload/delete flows, editable detail grid, and the create (POST + persist
// staged images) / edit (PUT) save — is carried over unchanged. The panel already had
// Create/Edit parity (mode derives from a missing UniqueID, as before).
import React, { useState, useMemo, useEffect } from 'react';
import { Trash2, Plus } from 'lucide-react';
import { useSwipeable } from 'react-swipeable';
import authAxios from '../utils/authAxios';
import SecureImage from './SecureImage';
import PidModal, { PrimaryButton, QuietButton, FieldError } from './PidModal';
import { ConfirmDelete } from '../styles/TableStyles';

import {
  PIDDisLayout,
  PIDDisMediaColumn,
  PIDDisDivider,
  PIDDisDetailsColumn,
  HighlightedTitle,
  InfoRow,
  InfoLabel,
  InfoValue,
  CarouselNavButton,
  CarouselWrapper,
  StyledInput,
  PIDDisDetailGrid,
  PIDDisImageBox,
  LightboxBackdrop,
  LightboxCloseButton,
  LightboxImage,
} from '../styles/PackageIndexStyles';

const parseDimensions = (dim) => {
  if (!dim) return { L: '', W: '', H: '' };
  const parts = String(dim).split(',').map(s => s.trim());
  return { L: parts[0] || '', W: parts[1] || '', H: parts[2] || '' };
};

const formatDimensions = ({ L, W, H }) => {
  const l = String(L || '').trim();
  const w = String(W || '').trim();
  const h = String(H || '').trim();
  if (!l && !w && !h) return '';
  return [l, w, h].join(',');
};

const PIDDISPLAYS_CDN_BASE = 'https://thevaultmedia.azureedge.net/vault-images/PIDDisplays/';
const toPidDisplaysCdnUrl = (src) => {
  if (!src) return '';
  const s = String(src);
  if (/^https?:\/\//i.test(s)) return s;
  const cleaned = s.split('?')[0].split('#')[0].trim();
  const filename = cleaned.split('/').filter(Boolean).pop() || cleaned;
  const encoded = encodeURIComponent(filename).replace(/%2F/g, '/');
  return `${PIDDISPLAYS_CDN_BASE}${encoded}`;
};

const PidDisplayModal = ({ group, onClose, onUpdated, onDelete }) => {
  const [current, setCurrent] = useState(0);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  const [description, setDescription] = useState(group?.Description || '');
  const [productId, setProductId] = useState(group?.ProductID || '');
  const [itemNumber, setItemNumber] = useState(group?.ItemNumber || '');
  const [countPerPallet, setCountPerPallet] = useState(group?.CountPerPallet ?? '');
  const [formedDims, setFormedDims] = useState(parseDimensions(group?.DimensionsFormed));
  const [flatDims, setFlatDims] = useState(parseDimensions(group?.DimensionsFlat));
  const [images, setImages] = useState(() => (Array.isArray(group?.Images) ? group.Images : []));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const isNew = !group?.UniqueID;

  const imagesToDisplay = useMemo(() => {
    if (Array.isArray(images) && images.length > 0) return images;
    return ['https://thevaultmedia.blob.core.windows.net/vault-images/PIDDisplays/DisplayFallback.webp'];
  }, [images]);

  const max = imagesToDisplay.length;

  useEffect(() => {
    imagesToDisplay.forEach(src => {
      const img = new Image();
      img.src = toPidDisplaysCdnUrl(src);
    });
  }, [imagesToDisplay]);

  /* While the lightbox is open, Escape closes it first — captured before PidModal's
     document-level Escape handler can close the whole modal. */
  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setLightboxSrc(null);
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [lightboxSrc]);

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => setCurrent((c) => (c + 1) % max),
    onSwipedRight: () => setCurrent((c) => (c - 1 + max) % max),
    trackMouse: true,
  });

  const handleDimChange = (setter) => (field, value) => {
    setter(prev => ({ ...prev, [field]: value }));
  };

  const handleUploadImages = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    try {
      const newImageNames = [];
      for (const file of files) {
        const form = new FormData();
        form.append('image', file);
        form.append('name', productId || description || file.name);

        const uploadRes = await authAxios.post('/uploadImage/PIDDisplays', form);
        const blobName = uploadRes.data?.blobName || '';
        const filename = blobName.split('/').pop();

        if (!isNew) {
          await authAxios.post('/api/piddisplays/images', {
            DisplayUniqueID: group.UniqueID,
            ImagePath: filename,
          });
        }

        if (filename) newImageNames.push(filename);
      }

      if (newImageNames.length) {
        setImages(prev => [...prev, ...newImageNames]);
        if (onUpdated && !isNew) {
          onUpdated({
            ...group,
            Images: [...images, ...newImageNames],
            Picture: images.length ? images[0] : newImageNames[0],
          });
        }
      }
    } catch (err) {
      console.error('[PidDisplayModal] upload error', err);
      setError('Failed to upload image(s).');
    } finally {
      event.target.value = '';
    }
  };

  const handleDeleteImage = async (filename) => {
    try {
      await authAxios.delete(`/api/piddisplays/images/${encodeURIComponent(filename)}`);
      const updated = images.filter(img => img !== filename);
      setImages(updated);
      if (onUpdated && !isNew) {
        onUpdated({
          ...group,
          Images: updated,
          Picture: updated[0] || '',
        });
      }
      if (current >= updated.length) {
        setCurrent(Math.max(0, updated.length - 1));
      }
    } catch (err) {
      console.error('[PidDisplayModal] delete error', err);
      setError('Failed to delete image.');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError('');
    try {
      const payload = {
        Description: description,
        ProductID: productId,
        ItemNumber: itemNumber,
        CountPerPallet: countPerPallet === '' ? null : Number(countPerPallet),
        DimensionsFormed: formatDimensions(formedDims),
        DimensionsFlat: formatDimensions(flatDims),
      };

      let id = group?.UniqueID;
      if (isNew) {
        const res = await authAxios.post('/api/piddisplays', payload);
        id = res.data?.insertedId;

        if (id && images.length) {
          await Promise.all(
            images.map((filename) =>
              authAxios.post('/api/piddisplays/images', {
                DisplayUniqueID: id,
                ImagePath: filename,
              })
            )
          );
        }
      } else {
        await authAxios.put(`/api/piddisplays/${group.UniqueID}`, payload);
      }

      if (onUpdated) {
        onUpdated({
          ...(group || {}),
          UniqueID: id,
          ...payload,
          Images: images,
          Picture: images[0] || '',
        });
      }
      onClose();
    } catch (err) {
      console.error('[PidDisplayModal] save error', err);
      if (err?.response?.status === 409) {
        setError('A display with this Description already exists.');
      } else {
        setError('Failed to save display.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const formedDisplay = useMemo(() => {
    const { L, W, H } = formedDims;
    if (!L && !W && !H) return '';
    return `${L || ''} x ${W || ''} x ${H || ''}`.trim();
  }, [formedDims]);

  const flatDisplay = useMemo(() => {
    const { L, W, H } = flatDims;
    if (!L && !W && !H) return '';
    return `${L || ''} x ${W || ''} x ${H || ''}`.trim();
  }, [flatDims]);

  return (
    <PidModal
      title={isNew ? 'New PID Display' : `Edit PID Display — ${productId || description || 'Display'}`}
      status={isNew ? 'Pending' : group?.Status}
      onClose={onClose}
      footerLeft={!isNew && onDelete ? (
        <ConfirmDelete onConfirm={onDelete} label="Delete" disabled={isSaving} />
      ) : null}
      footerRight={(
        <>
          <QuietButton type="button" onClick={onClose} disabled={isSaving}>Cancel</QuietButton>
          <PrimaryButton type="button" onClick={handleSave} disabled={isSaving}>Save</PrimaryButton>
        </>
      )}
    >
      <PIDDisLayout>
        <PIDDisMediaColumn>
          <CarouselWrapper {...swipeHandlers}>
            <div style={{ position: 'relative', width: '100%' }}>
              {max > 1 && current > 0 && (
                <CarouselNavButton
                  left
                  onClick={() => setCurrent((current - 1 + max) % max)}
                >
                  &#8249;
                </CarouselNavButton>
              )}
              {max > 1 && current < max - 1 && (
                <CarouselNavButton
                  right
                  onClick={() => setCurrent((current + 1) % max)}
                >
                  &#8250;
                </CarouselNavButton>
              )}

              <div style={{ position: 'relative', width: '100%' }}>
                <PIDDisImageBox className="PIDDis-ImageBox">
                  <SecureImage
                    src={imagesToDisplay[current]}
                    folder="PIDDisplays"
                    alt={productId || description || 'Display image'}
                    onClick={() => setLightboxSrc(toPidDisplaysCdnUrl(imagesToDisplay[current]))}
                  />
                </PIDDisImageBox>
                {images.length > 0 && (
                  <button
                    type="button"
                    onClick={() => handleDeleteImage(imagesToDisplay[current])}
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      background: 'var(--shadow-a55)',
                      borderRadius: '999px',
                      border: 'none',
                      padding: '4px',
                      cursor: 'pointer',
                      color: 'var(--white)',
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              {max > 1 && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: '10px',
                    right: '12px',
                    color: 'var(--white)',
                    fontSize: '0.85rem',
                    background: 'var(--shadow-a35)',
                    padding: '2px 6px',
                    borderRadius: '6px',
                  }}
                >
                  {current + 1} / {max}
                </div>
              )}
            </div>
          </CarouselWrapper>

          <div style={{ marginTop: '0.25rem' }}>
            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.35rem',
                padding: '0.4rem 0.75rem',
                borderRadius: '999px',
                background: 'var(--green-600)',
                color: 'var(--white)',
                cursor: 'pointer',
                fontSize: '0.9rem',
              }}
            >
              <Plus size={16} />
              Upload Image
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                onChange={handleUploadImages}
                style={{ display: 'none' }}
              />
            </label>
          </div>
        </PIDDisMediaColumn>

        <PIDDisDivider />

        <PIDDisDetailsColumn>
          <HighlightedTitle>Display Details</HighlightedTitle>
          <PIDDisDetailGrid className="PIDDis-DetailGrid">
            <InfoRow style={{ gridColumn: '1 / -1' }}>
              <InfoLabel>Description</InfoLabel>
              <StyledInput
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Description"
              />
            </InfoRow>

            <InfoRow style={{ gridColumn: '1 / -1' }}>
              <InfoLabel>Product ID</InfoLabel>
              <StyledInput
                value={productId}
                onChange={e => setProductId(e.target.value)}
                placeholder="Product ID"
              />
            </InfoRow>

            <InfoRow>
              <InfoLabel>Item Number</InfoLabel>
              <StyledInput
                value={itemNumber}
                onChange={e => setItemNumber(e.target.value)}
                placeholder="Item Number"
              />
            </InfoRow>

            <InfoRow>
              <InfoLabel>Count (Per Pallet)</InfoLabel>
              <StyledInput
                type="number"
                value={countPerPallet}
                onChange={e => setCountPerPallet(e.target.value)}
                placeholder="0"
              />
            </InfoRow>

            <InfoRow style={{ gridColumn: '1 / -1' }}>
              <InfoLabel>Dimensions (Formed)</InfoLabel>
              <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
                <StyledInput
                  value={formedDims.L}
                  onChange={e => handleDimChange(setFormedDims)('L', e.target.value)}
                  placeholder="L"
                />
                <StyledInput
                  value={formedDims.W}
                  onChange={e => handleDimChange(setFormedDims)('W', e.target.value)}
                  placeholder="W"
                />
                <StyledInput
                  value={formedDims.H}
                  onChange={e => handleDimChange(setFormedDims)('H', e.target.value)}
                  placeholder="H"
                />
              </div>
              {formedDisplay && (
                <InfoValue>{formedDisplay}</InfoValue>
              )}
            </InfoRow>

            <InfoRow style={{ gridColumn: '1 / -1' }}>
              <InfoLabel>Dimensions (Flat)</InfoLabel>
              <div style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
                <StyledInput
                  value={flatDims.L}
                  onChange={e => handleDimChange(setFlatDims)('L', e.target.value)}
                  placeholder="L"
                />
                <StyledInput
                  value={flatDims.W}
                  onChange={e => handleDimChange(setFlatDims)('W', e.target.value)}
                  placeholder="W"
                />
                <StyledInput
                  value={flatDims.H}
                  onChange={e => handleDimChange(setFlatDims)('H', e.target.value)}
                  placeholder="H"
                />
              </div>
              {flatDisplay && (
                <InfoValue>{flatDisplay}</InfoValue>
              )}
            </InfoRow>
          </PIDDisDetailGrid>

          {error && <FieldError style={{ marginTop: '0.5rem' }}>{error}</FieldError>}
        </PIDDisDetailsColumn>
      </PIDDisLayout>

      {lightboxSrc && (
        <LightboxBackdrop
          className="PIDDis-LightboxBackdrop"
          onClick={() => setLightboxSrc(null)}
          role="dialog"
          aria-modal="true"
        >
          <LightboxCloseButton
            type="button"
            className="PIDDis-LightboxCloseButton"
            aria-label="Close preview"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxSrc(null);
            }}
          >
            ×
          </LightboxCloseButton>
          <div onClick={(e) => e.stopPropagation()}>
            <LightboxImage src={lightboxSrc} alt="Preview" />
          </div>
        </LightboxBackdrop>
      )}
    </PidModal>
  );
};

export default PidDisplayModal;
